import isEqual from 'fast-deep-equal';
import type {
  DocumentTranscriptParagraphV1,
  DocumentTranscriptTextV1,
  DocumentTranscriptV1
} from '../types/editors/transcripts/transcript.ts';

interface StoredText {
  text: string;
  children: StoredText[] | null;
  bold: boolean | null;
  italic: boolean | null;
  underlined: boolean | null;
  strike: boolean | null;
  color: string | null;
  mark: boolean | null;
  start: number | null;
  stop: number | null;
}

// Match the fields persisted by the API's DocumentTranscriptV1 model_dump.
// Missing optional properties become null; editor/search IDs are not stored.
function storedText(node: DocumentTranscriptTextV1): StoredText {
  return {
    text: node.text,
    children: Array.isArray(node.children)
      ? node.children.map(storedText)
      : null,
    bold: node.bold ?? null,
    italic: node.italic ?? null,
    underlined: node.underlined ?? null,
    strike: node.strike ?? null,
    color: node.color ?? null,
    mark: node.mark ?? null,
    start: node.start ?? null,
    stop: node.stop ?? null
  };
}

function storedParagraphs(nodes: DocumentTranscriptParagraphV1[]) {
  return nodes.map(node => {
    if (!Array.isArray(node.children)) return node;
    return {
      type: node.type ?? 'paragraph',
      start: node.start ?? null,
      stop: node.stop ?? null,
      children: node.children.map(storedText)
    };
  });
}

function storedDocument(doc: DocumentTranscriptV1 | undefined) {
  if (!doc || !Array.isArray(doc.document)) return doc;
  return {
    document_type: doc.document_type ?? 'document',
    version: doc.version ?? 'v1',
    document: doc.document.map(node => {
      if (!Array.isArray(node.children)) return node;
      return {
        type: node.type ?? 'utterance',
        start: node.start ?? null,
        stop: node.stop ?? null,
        speaker: node.speaker ?? null,
        children: storedParagraphs(node.children)
      };
    })
  };
}

export function transcriptDocumentsEqual(
  left: DocumentTranscriptV1 | undefined,
  right: DocumentTranscriptV1 | undefined
): boolean {
  return isEqual(storedDocument(left), storedDocument(right));
}

export function transcriptParagraphsEqual(
  left: DocumentTranscriptParagraphV1[],
  right: DocumentTranscriptParagraphV1[]
): boolean {
  return isEqual(storedParagraphs(left), storedParagraphs(right));
}
